import React from 'react';

interface ReentryConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onViewProfile?: () => void;
  /**
   * What the dialog SAYS. Kept separate from what confirming it DOES: 'active_role' takes the
   * same code path as 'awaiting_entry' (the old role is removed before the new visit), but the
   * person is not at the gate waiting — they hold a liberação that is simply still open. Captioning
   * that "AGUARDANDO ENTRADA" contradicted the person's own screen, which read "Sem visita".
   */
  variant?: 'inside' | 'awaiting_entry' | 'active_role';
  visitorInfo: {
    personName: string;
    unit: string;
    entryTime: string;
  };
}

export function ReentryConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  onViewProfile,
  variant = 'inside',
  visitorInfo
}: ReentryConfirmationModalProps) {
  if (!isOpen) return null;

  const isInside = variant === 'inside';
  const isAwaiting = variant === 'awaiting_entry';

  const formatDateTime = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return dateString; }
  };

  const calculateTimeElapsed = (entryTime: string): string => {
    try {
      const diffMs = Date.now() - new Date(entryTime).getTime();
      const hours = Math.floor(diffMs / 3600000);
      const minutes = Math.floor((diffMs % 3600000) / 60000);
      return hours > 0 ? `${hours}h ${minutes}min` : `${minutes} min`;
    } catch { return ''; }
  };

  const accentColor = isInside ? '#ef4444' : '#f59e0b';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xs mx-4 overflow-hidden animate-fadeIn">

        {/* Accent bar */}
        <div style={{ backgroundColor: accentColor, height: 4 }} />

        <div className="px-6 pt-5 pb-6 space-y-4">

          {/* Status badge + name */}
          <div className="text-center">
            <span
              className="inline-block px-3 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider mb-2"
              style={{ backgroundColor: isInside ? '#fee2e2' : '#fef3c7', color: accentColor }}
            >
              {isInside ? 'Já está dentro' : isAwaiting ? 'Aguardando entrada' : 'Liberação em aberto'}
            </span>
            <h2 className="text-xl font-bold text-gray-900">{visitorInfo.personName}</h2>
          </div>

          {/* Info row — text only, highlighted */}
          <div className="flex gap-3 text-center">
            <div className="flex-1 bg-gray-50 rounded-xl px-3 py-3">
              <p className="text-xs text-gray-400 mb-0.5">Unidade</p>
              <p className="text-base font-bold text-gray-900 truncate">{visitorInfo.unit || '—'}</p>
            </div>
            <div className="flex-1 bg-gray-50 rounded-xl px-3 py-3">
              <p className="text-xs text-gray-400 mb-0.5">{isInside ? 'Dentro há' : 'Registrado'}</p>
              <p className="text-base font-bold text-gray-900 truncate">
                {isInside ? calculateTimeElapsed(visitorInfo.entryTime) : formatDateTime(visitorInfo.entryTime)}
              </p>
            </div>
          </div>

          {/* Ver Perfil */}
          {onViewProfile && (
            <button
              type="button"
              onClick={onViewProfile}
              className="w-full px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm transition-colors"
            >
              Ver Perfil do Visitante
            </button>
          )}

          {/* Question */}
          <div className="border-t border-gray-100 pt-3 text-center">
            <p className="text-sm font-semibold text-gray-800 mb-0.5">Registrar uma nova visita?</p>
            <p className="text-xs text-gray-400">
              {isAwaiting ? 'O registro atual será cancelado' : 'A visita atual será finalizada automaticamente'}
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium text-sm transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="flex-1 px-4 py-2.5 text-white rounded-xl font-semibold text-sm transition-colors shadow-sm"
              style={{ backgroundColor: accentColor }}
            >
              Sim, nova visita
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

// Inject animation if needed
if (typeof document !== 'undefined' && !document.head.querySelector('style[data-reentry-modal]')) {
  const s = document.createElement('style');
  s.setAttribute('data-reentry-modal', 'true');
  s.textContent = `@keyframes fadeIn{from{opacity:0;transform:scale(.95)}to{opacity:1;transform:scale(1)}}.animate-fadeIn{animation:fadeIn .2s ease-out}`;
  document.head.appendChild(s);
}
